/**
 * Static prompt banks for the four Yadid Advisory engagement playbooks.
 * Kept in a plain JS module (no JSX) so the giant const doesn't bloat
 * ConveningPlaybook.jsx and so future edits are one-file scoped.
 *
 * DB overrides: Settings → Playbooks lets partners fork any of these.
 * `fetchPlaybook(engagement_type)` returns the active override if one exists,
 * else the canonical version below. All icons are Phosphor components looked
 * up by string name so we can round-trip through the API.
 */
import {
  UsersThree, ListChecks, Handshake, Compass, Lightning, Trophy,
  ChatCircleDots, Path, GraduationCap, Circle,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";

/** Icon name → component. Extend when new icons are used server-side. */
export const ICON_MAP = {
  UsersThree, ListChecks, Handshake, Compass, Lightning, Trophy,
  ChatCircleDots, Path, GraduationCap, Circle,
};

export const iconFor = (name) => ICON_MAP[name] || Circle;

export const PLAYBOOKS = {
  trusted_advisor: {
    label: "Trusted Advisor Playbook",
    blurb: "Sustaining continuity of counsel across a leader's arc",
    sections: [
      {
        key: "relationship_cadence",
        label: "Relationship cadence",
        icon: Compass,
        blurb: "The rhythm that keeps trust warm and current",
        prompts: [
          "Set the monthly principal-to-principal session — same time, protected on both calendars",
          "Define the between-session escalation path: what warrants an unscheduled call",
          "Establish a shared doc for live decisions where the leader can drop context anytime",
          "Every quarter, review whether the cadence still fits the leader's actual rhythm",
          "Annually, rewrite the relationship charter so both sides re-consent to the terms",
        ],
      },
      {
        key: "live_decisions",
        label: "Live decisions",
        icon: Lightning,
        blurb: "Where the leader currently needs sharper judgment",
        prompts: [
          "Log the two or three decisions the leader is chewing on this quarter",
          "For each, note the stakeholder whose reaction the leader most cares about",
          "Frame the option set — including the option the leader hasn't yet considered",
          "After each decision, capture what actually shaped the choice so the pattern is visible",
          "Codify which decisions the leader should stop bringing to you (they've grown past it)",
        ],
      },
      {
        key: "network_cultivation",
        label: "Network cultivation",
        icon: Trophy,
        blurb: "Warm introductions and strategic proximity",
        prompts: [
          "Map the 5–8 people whose proximity would compound the leader's impact",
          "For each, propose the introduction path — mutual, event, or direct outreach",
          "Debrief every intro made: what did the leader learn, what will they do with it",
          "Cultivate one 'oxygen' relationship per quarter with no ROI expectation",
          "Twice a year, ask the leader who they want to be known by three years from now",
        ],
      },
    ],
  },
  strategic_initiative: {
    label: "Strategic Initiative Playbook",
    blurb: "Advisory arc from opportunity framing to institutional lift",
    sections: [
      {
        key: "opportunity_framing",
        label: "Opportunity framing",
        icon: Compass,
        blurb: "The win worth chasing, articulated in the leader's own words",
        prompts: [
          "Write the win: what specifically becomes true if this initiative succeeds",
          "Name the theory of the case — why this bet, why now, why by us",
          "List the three success measures the board would recognize as a win",
          "Identify the one assumption most likely to prove wrong under pressure",
          "Test the framing with a skeptic before it's socialized more broadly",
        ],
      },
      {
        key: "strategy_and_sequencing",
        label: "Strategy & sequencing",
        icon: ListChecks,
        blurb: "The order of moves that capture the opportunity",
        prompts: [
          "Sequence the workstreams: what must ship first for later work to matter",
          "Design the 90-day proof point that validates the theory of the case",
          "Identify the resource unlock (people, budget, permissions) each stage needs",
          "Build the kill-switch criteria — what would tell you to stop, and who decides",
          "Cast the leadership team into named roles for each phase of the arc",
        ],
      },
      {
        key: "stakeholder_engagement",
        label: "Stakeholder engagement",
        icon: UsersThree,
        blurb: "Whose alignment unlocks it — and how to earn it",
        prompts: [
          "Map the stakeholder set: sponsors, blockers, silent influencers",
          "For each blocker, articulate their real objection (usually not the stated one)",
          "Sequence outreach: who moves first, whose no would freeze everything",
          "Prepare the leader for the three toughest questions each stakeholder will ask",
          "Build the artifact each stakeholder needs to say yes to their own peers",
        ],
      },
      {
        key: "institutional_positioning",
        label: "Institutional positioning",
        icon: Trophy,
        blurb: "Sustaining the outcome after the initiative ships",
        prompts: [
          "Define the narrative the org tells about the initiative six months in",
          "Identify who inside the org will carry the story after your engagement ends",
          "Plan the board / external moment where the outcome gets crystallized",
          "Document what the org must institutionalize to prevent regression",
          "Debrief with the sponsor: what surprised them, what to do differently next time",
        ],
      },
    ],
  },
  executive_convening: {
    label: "Executive Convening Playbook",
    blurb: "Right leaders, right room, right follow-through",
    sections: [
      {
        key: "participant_strategy",
        label: "Participant strategy",
        icon: UsersThree,
        blurb: "Right leaders, right room, right invitation",
        prompts: [
          "Define the singular purpose that justifies leaders spending time in this room",
          "Map the 8–12 people whose alignment moves the mission forward this quarter",
          "For each, identify what they need to hear AND what they need to feel heard on",
          "Sequence the outreach: who invites whom, in what order, using whose relationship",
          "Anticipate the two invitees whose absence would break the room, and secure them first",
        ],
      },
      {
        key: "agenda_arc",
        label: "Agenda arc",
        icon: ListChecks,
        blurb: "Shared frame → hard truth → commitment",
        prompts: [
          "Open with a shared frame every participant recognizes as their own stake",
          "Build one hard truth into the middle so the group earns the right to decide together",
          "Design an inflection where dissent is invited before consensus is offered",
          "End with a specific commitment structure — who will do what by when",
          "Reserve 20% of runtime as unstructured connection; the relationship is the deliverable",
        ],
      },
      {
        key: "follow_on",
        label: "Follow-on cultivation",
        icon: Handshake,
        blurb: "Momentum after the room clears",
        prompts: [
          "Send a personal note to each participant within 48 hours, referencing what they said",
          "Publish the shared commitments in writing so drift is visible",
          "Schedule the 1:1s that the convening surfaced but couldn't complete",
          "Track the initiative each participant owns — who's carrying momentum, who isn't",
          "Debrief with the host: what earned trust, what to change for the next convening",
        ],
      },
    ],
  },
  executive_communication: {
    label: "Executive Comms Playbook",
    blurb: "From message architecture to the moment itself",
    sections: [
      {
        key: "message_architecture",
        label: "Message architecture",
        icon: ChatCircleDots,
        blurb: "Core narrative, proof points, refrains",
        prompts: [
          "Distill the leader's message to one sentence they can say without notes",
          "Choose three proof points that would land with the hardest listener in the room",
          "Design the refrain that a headline writer would quote from the remarks",
          "Anticipate the counter-message and address it before it can be raised",
          "Cut anything the leader doesn't fully believe — the audience always hears it",
        ],
      },
      {
        key: "speaker_prep",
        label: "Speaker prep",
        icon: GraduationCap,
        blurb: "Prepared voice, ready for the room",
        prompts: [
          "Rehearse the opening three sentences until they feel spoken, not read",
          "Prepare the leader for the three toughest questions and one they'll hope for",
          "Coach the pause: where should the leader stop and let the room absorb",
          "Define the tone shift between formal remarks and open dialogue",
          "Confirm the exit line — how does the leader end the moment on their own terms",
        ],
      },
      {
        key: "post_moment",
        label: "Post-moment lift",
        icon: Path,
        blurb: "What to carry into the next moment",
        prompts: [
          "Debrief within 48 hours while the moment is still emotionally accurate",
          "Extract the one line the audience actually repeated back to each other",
          "Identify what the leader would do differently if the moment repeated tomorrow",
          "Feed the insight into the leader's message architecture for the next moment",
          "Document what the org should institutionalize from this moment (rituals, phrases, artifacts)",
        ],
      },
    ],
  },
};

/** Merge stored progress with the canonical prompt bank for the section list.
 *
 * Iter 85.32 · Also carries through DB-stored sections whose keys aren't in
 * the canonical template. This happens when the Briefing Studio applies a
 * playbook item with a section_key from a different engagement_type
 * (e.g. the AI proposed an executive_convening section on a
 * strategic_initiative engagement). Without this pass those items would
 * be silently dropped from the UI even though the backend committed them.
 * Orphan sections carry an `_orphan: true` marker so `ConveningPlaybook`
 * can render them under a dedicated "Additional playbook items" heading.
 */
export function ensureShape(stored, sections) {
  const out = {};
  const canonicalKeys = new Set(sections.map((s) => s.key));
  for (const s of sections) {
    const existing = stored?.[s.key] || [];
    if (existing.length > 0) {
      out[s.key] = existing.map((it) => ({ prompt: it.prompt || "", note: it.note || "", done: !!it.done, source_briefing_id: it.source_briefing_id || null }));
    } else {
      out[s.key] = s.prompts.map((p) => ({ prompt: p, note: "", done: false, source_briefing_id: null }));
    }
  }
  // Preserve any DB-stored sections that aren't in the canonical template
  // so briefing-applied items on non-matching section_keys remain visible.
  if (stored && typeof stored === "object") {
    for (const [k, arr] of Object.entries(stored)) {
      if (canonicalKeys.has(k)) continue;
      if (!Array.isArray(arr) || arr.length === 0) continue;
      out[k] = arr.map((it) => ({ prompt: it.prompt || "", note: it.note || "", done: !!it.done, source_briefing_id: it.source_briefing_id || null, _orphan: true }));
    }
  }
  return out;
}

/**
 * Fetch the active playbook for an engagement type — DB override if one exists,
 * canonical otherwise. Returns the same shape as `PLAYBOOKS[type]` so the
 * consumer can drop it straight into `ensureShape`. Icons are attached
 * client-side via `iconFor(icon_name)`.
 */
export async function fetchPlaybook(engagementType) {
  if (!engagementType) return null;
  try {
    const { data } = await api.get(`/playbooks/${engagementType}`);
    return {
      label: data.label,
      blurb: data.blurb,
      sections: (data.sections || []).map((s) => ({
        key: s.key,
        label: s.label,
        blurb: s.blurb || "",
        icon: iconFor(s.icon_name),
        prompts: s.prompts || [],
      })),
      _version: data.version,
      _is_canonical: !!data.is_canonical,
    };
  } catch {
    // Fall through to bundled static template — keeps the playbook UI alive
    // even if the API blips.
    return PLAYBOOKS[engagementType] || null;
  }
}
